/* eslint-disable camelcase */ /* TODO: should the API be camelCase? */
import React, { useEffect, useState } from 'react'

import { track } from 'lib/analytics'

import { getPlacementScreen } from 'lib/placements/getPlacementScreen'
import { PlacementAd, filteredAds } from 'lib/placements/filteredAds'

import { placementsDecisionPath } from 'lib/urls'
import fetch from 'lib/fetch'
import { useQuery } from '@tanstack/react-query'
import withReactQueryClientProvider from 'shared/HOC/withReactQueryClientProvider'
import Image from 'components/image'
// eslint-disable-next-line import/no-extraneous-dependencies
import { Button, Modal } from 'baby-design'
import { useMediaQuery, useTheme } from '@material-ui/core'
import { registryProperties } from 'src/lib/tracking/shared/bundles/registryProperties'
import useOnCurrentUserLoaded from 'shared/hooks/useOnCurrentUserLoaded/useOnCurrentUserLoaded'
import css from './PlacementInterstitialModal.scss'

interface AdProps {
  ad: PlacementAd
  unitId: string
  show: boolean
  onHide: () => void
  eventLocation: string
}

export interface PlacementsInterstitialModalProps {
  unitId: string
  eventLocation: string
}

const Ad: React.FC<AdProps> = ({ unitId, ad, show, onHide, eventLocation }) => {
  let trackingContext = {
    eventLocation,
    adId: ad.adId,
    adTitle: ad.content.title,
    adUnitId: unitId,
    adCtaText: ad.content.ctaText || '',
    eventUrl: '',
  }
  useOnCurrentUserLoaded((currentUser) => {
    trackingContext = {
      ...trackingContext,
      ...registryProperties(currentUser),
      eventUrl: window?.location?.href,
    }
    track.targetedPlacementViewed(trackingContext)
  })

  useEffect(() => {
    if (ad.tracking?.impressionUrl) {
      fetch(ad.tracking.impressionUrl, { method: 'POST' })
    }
  }, [ad.tracking?.impressionUrl])

  const handleCtaClick = () => {
    if (ad.tracking?.ctaClickedUrl) {
      fetch(ad.tracking.ctaClickedUrl, { method: 'POST' })
    }
    track.targetedPlacementClicked(trackingContext)
  }
  const handleSecondaryCtaClick = () => {
    if (ad.tracking?.secondaryCtaClickedUrl) {
      fetch(ad.tracking.secondaryCtaClickedUrl, { method: 'POST' })
    }
    track.targetedPlacementClicked({
      ...trackingContext,
      adCtaText: ad.content.secondaryCtaText || '',
    })
  }
  const handleOnHide = () => {
    track.targetedPlacementDismissed(trackingContext)
    onHide()
  }
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const { content, contentType } = ad
  const interstitialBigImage = 'placementInterstitialFullImage'
  const fullImage = contentType === interstitialBigImage

  const image = (
    <Image
      className={fullImage ? css.fullImage : css.icon}
      role="presentation"
      src={content.imageUrl}
    />
  )
  const primaryButton = (
    <Button
      block
      className={css.primaryCta}
      href={content.ctaUrl}
      size={isMobile ? 'md' : 'lg'}
      target="_blank"
      variant="primary"
      onClick={handleCtaClick}
    >
      {content.ctaText}
    </Button>
  )

  const secondaryButton = content.secondaryCtaUrl ? (
    <Button
      block
      className={css.secondaryCta}
      href={content.secondaryCtaUrl}
      size={isMobile ? 'md' : 'lg'}
      target="_blank"
      variant="secondary"
      onClick={handleSecondaryCtaClick}
    >
      {content.secondaryCtaText}
    </Button>
  ) : null

  return (
    <Modal
      showCloseButton
      handleClose={handleOnHide}
      icon={fullImage ? null : image} // Icon only passed if its not a full image
      iconAlignment={isMobile ? 'center' : 'left'}
      isOpen={show}
      primaryButton={primaryButton}
      secondaryButton={secondaryButton}
    >
      <>
        {fullImage ? image : null}
        <h1>{content.title}</h1>
        <h2>{content.body}</h2>
      </>
    </Modal>
  )
}

const PlacementsInterstitialModal: React.FC<
  PlacementsInterstitialModalProps
> = ({ unitId, eventLocation }) => {
  const [show, setShow] = useState(false)
  const [currentAd, setCurrentAd] = useState<PlacementAd | null>(null)
  const onHide = () => setShow(false)

  let pathname = '/'
  let host = 'www.babylist.com'

  if (typeof window !== 'undefined') {
    pathname = window.location.pathname
    // eslint-disable-next-line prefer-destructuring
    host = window.location.host
  }

  const requestPath = `${placementsDecisionPath}?unit_ids=${unitId}`
  const result = useQuery({
    queryKey: [`placementInterstitial-${unitId}`, requestPath],
    queryFn: () => fetch(requestPath, { method: 'GET' }),
    retry: false,
    staleTime: 0,
  })

  useEffect(() => {
    if (result.isLoading) {
      return
    }
    if (result.isError) {
      console.error('Error fetching ads', result.error)
      return
    }
    const { decisions } = result.data
    let ads = []
    if (decisions && decisions.length) {
      ads = decisions[0].ads
    }
    if (ads.length > 0) {
      const currentScreen = getPlacementScreen(pathname, host)
      ads = filteredAds(ads, currentScreen)
      setCurrentAd(ads[0])
      setShow(true)
    }
  }, [result.isLoading])

  if (!currentAd) {
    return null
  }

  return (
    <Ad
      ad={currentAd}
      eventLocation={eventLocation}
      show={show}
      unitId={unitId}
      onHide={onHide}
    />
  )
}

export default withReactQueryClientProvider(PlacementsInterstitialModal)
